/**
 * 按印花组将合成结果按主图 / 细节图层级分组（与 listingBridgeColorSlots 一致）。
 */

import {
  getDedupedCompositeResultsForGroup,
} from '@/utils/goodsCompositeSelection';
import {
  groupSlidesByMain,
  enrichSlideHierarchyFields,
  TEMPLATE_IMAGE_ROLE_DETAIL,
  TEMPLATE_IMAGE_ROLE_MAIN,
} from '@/image-designer/utils/templateImageHierarchy';
import { resolveExportPreviewImageColumn } from './buildGoodsExportPreviewData';
import { pickGoodsCompositeUrl } from './listingBridgeColorSlots';

function trimText(value) {
  return String(value || '').trim();
}

function templateImageById(form, imageId) {
  const images = form?.templateData?.images || [];
  return images.find((img) => String(img?.id) === String(imageId)) || null;
}

/** 模板幻灯片上可用于透传展示的地址（无印花合成时用底图） */
export function pickTemplateSlidePassThroughUrl(templateImage) {
  if (!templateImage || typeof templateImage !== 'object') return '';
  const candidates = [
    templateImage.viewUrl,
    templateImage.displayUrl,
    templateImage.urlDisplay,
    templateImage.url,
    templateImage.src,
    templateImage.imageUrl,
    templateImage.dataUrl,
    templateImage.thumbUrl,
  ];
  for (const value of candidates) {
    const s = trimText(value);
    if (!s || s.startsWith('blob:') || s.startsWith('COMP-')) continue;
    return s;
  }
  return '';
}

function resolveResultRole(result, form, templateImage, billingNav) {
  const kind = trimText(result?.kind);
  if (kind === TEMPLATE_IMAGE_ROLE_MAIN || kind === TEMPLATE_IMAGE_ROLE_DETAIL) return kind;
  const role = trimText(templateImage?.role);
  if (role === TEMPLATE_IMAGE_ROLE_DETAIL || role === TEMPLATE_IMAGE_ROLE_MAIN) return role;
  const column = resolveExportPreviewImageColumn(result, form, billingNav);
  return column === TEMPLATE_IMAGE_ROLE_DETAIL
    ? TEMPLATE_IMAGE_ROLE_DETAIL
    : TEMPLATE_IMAGE_ROLE_MAIN;
}

/**
 * @returns {{
 *   groups: Array<{
 *     main: { result, resultIndex, templateImage },
 *     details: Array<{ result, resultIndex, templateImage }>,
 *     mainAsDetailFallback?: boolean,
 *   }>,
 *   hasHierarchy: boolean,
 *   useHierarchyLayout: boolean,
 * }}
 */
export function groupGoodsCompositeResultsByMain(form, groupIndex, options = {}) {
  const billingNav = options.billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods';
  const deduped = getDedupedCompositeResultsForGroup(form, groupIndex);
  const templateImages = (form?.templateData?.images || []).map((img) => enrichSlideHierarchyFields(img));
  const hierarchyGroups = groupSlidesByMain(templateImages);
  const templateHasDetailSlides = hierarchyGroups.some((group) => group.details.length > 0);

  const resultByImageId = new Map();
  deduped.forEach((result, resultIndex) => {
    const imageId = trimText(result?.imageId);
    if (!imageId) return;
    resultByImageId.set(imageId, { result, resultIndex });
  });

  const groups = [];
  const consumedIndexes = new Set();

  if (templateHasDetailSlides || hierarchyGroups.length > 1) {
    hierarchyGroups.forEach(({ main, details }) => {
      const mainHit = resultByImageId.get(String(main.id));
      const detailHits = [];
      details.forEach((detailTpl) => {
        const hit = resultByImageId.get(String(detailTpl.id));
        if (!hit || !pickGoodsCompositeUrl(hit.result)) return;
        detailHits.push({
          result: hit.result,
          resultIndex: hit.resultIndex,
          templateImage: detailTpl,
        });
        consumedIndexes.add(hit.resultIndex);
      });

      if (mainHit && pickGoodsCompositeUrl(mainHit.result)) {
        groups.push({
          main: {
            result: mainHit.result,
            resultIndex: mainHit.resultIndex,
            templateImage: main,
          },
          details: detailHits,
        });
        consumedIndexes.add(mainHit.resultIndex);
        return;
      }

      if (detailHits.length > 0) {
        groups.push({
          main: detailHits[0],
          details: detailHits.slice(1),
          mainAsDetailFallback: true,
        });
        detailHits.forEach((hit) => consumedIndexes.add(hit.resultIndex));
      }
    });
  }

  deduped.forEach((result, resultIndex) => {
    if (consumedIndexes.has(resultIndex)) return;
    if (!pickGoodsCompositeUrl(result)) return;
    const tpl = templateImageById(form, result.imageId);
    const enrichedTpl = tpl ? enrichSlideHierarchyFields(tpl) : null;
    const role = resolveResultRole(result, form, enrichedTpl, billingNav);
    if (role === TEMPLATE_IMAGE_ROLE_DETAIL) return;
    groups.push({
      main: {
        result,
        resultIndex,
        templateImage: enrichedTpl,
      },
      details: [],
    });
    consumedIndexes.add(resultIndex);
  });

  const orphanDetails = [];
  deduped.forEach((result, resultIndex) => {
    if (consumedIndexes.has(resultIndex)) return;
    if (!pickGoodsCompositeUrl(result)) return;
    const tpl = templateImageById(form, result.imageId);
    const enrichedTpl = tpl ? enrichSlideHierarchyFields(tpl) : null;
    const role = resolveResultRole(result, form, enrichedTpl, billingNav);
    if (role !== TEMPLATE_IMAGE_ROLE_DETAIL) return;
    orphanDetails.push({
      result,
      resultIndex,
      templateImage: enrichedTpl,
    });
    consumedIndexes.add(resultIndex);
  });

  if (orphanDetails.length > 0) {
    if (groups.length > 0) {
      groups[0].details.push(...orphanDetails);
    } else {
      groups.push({
        main: orphanDetails[0],
        details: orphanDetails.slice(1),
        mainAsDetailFallback: true,
      });
    }
  }

  const hasHierarchy = groups.some((group) => group.details.length > 0);
  const useHierarchyLayout = templateHasDetailSlides || hasHierarchy;

  return {
    groups,
    hasHierarchy,
    useHierarchyLayout,
  };
}

/**
 * 详情 hydrate：模板有细节图但合成结果缺失时（常见于未画印花区），透传模板底图写入 compositeResults。
 * @param {object[]} forms
 * @returns {Promise<object[]>}
 */
export async function ensurePassThroughDetailComposites(forms) {
  if (!Array.isArray(forms) || forms.length === 0) return forms;

  const { buildTemplateSlideUrlMap } = await import('./goodsTemplateImageUrls');
  const nextForms = [];

  for (const form of forms) {
    const images = (form?.templateData?.images || []).map((img) => enrichSlideHierarchyFields(img));
    const hierarchyGroups = groupSlidesByMain(images);
    const detailTemplates = hierarchyGroups.flatMap((group) => group.details || []);
    if (detailTemplates.length === 0) {
      nextForms.push(form);
      continue;
    }

    const groupCount = Math.max(1, Array.isArray(form.imageGroups) ? form.imageGroups.length : 0);
    const results = Array.isArray(form.compositeResults) ? [...form.compositeResults] : [];

    const missingDetails = [];
    for (let gi = 0; gi < groupCount; gi += 1) {
      detailTemplates.forEach((detailTpl) => {
        const exists = results.some(
          (row) => Number(row?.groupIndex ?? 0) === gi
            && String(row?.imageId || '') === String(detailTpl.id)
            && Boolean(pickGoodsCompositeUrl(row)),
        );
        if (!exists) missingDetails.push({ gi, detailTpl });
      });
    }
    if (missingDetails.length === 0) {
      nextForms.push(form);
      continue;
    }

    let slideUrlMap = {};
    const needsFetch = missingDetails.some(({ detailTpl }) => !pickTemplateSlidePassThroughUrl(detailTpl));
    if (needsFetch && form.templateId) {
      try {
        slideUrlMap = await buildTemplateSlideUrlMap(
          form.templateId,
          images,
          form.templateData,
        );
      } catch (error) {
        console.warn('[商品详情] 细节图透传拉取模板图失败', error);
      }
    }

    let changed = false;
    missingDetails.forEach(({ gi, detailTpl }) => {
      const url = pickTemplateSlidePassThroughUrl(detailTpl)
        || trimText(slideUrlMap[detailTpl.id]);
      if (!url) return;
      results.push({
        imageId: detailTpl.id,
        compositeUrl: url,
        groupIndex: gi,
        kind: TEMPLATE_IMAGE_ROLE_DETAIL,
        passThrough: true,
      });
      changed = true;
    });

    nextForms.push(changed ? { ...form, compositeResults: results } : form);
  }

  return nextForms;
}

/**
 * 导出 / 预览：每组主图 SKC 对应的结果索引列表（主图 + 其细节图）。
 */
export function collectResultIndexesByMainSkc(form, groupIndex, options = {}) {
  const { groups } = groupGoodsCompositeResultsByMain(form, groupIndex, options);
  const selectedSet = Array.isArray(options.selectedResultIndexes)
    ? new Set(options.selectedResultIndexes.map(Number))
    : null;

  return groups
    .map((group) => {
      const indexes = [group.main.resultIndex, ...group.details.map((d) => d.resultIndex)];
      const filtered = selectedSet
        ? indexes.filter((ri) => selectedSet.has(ri))
        : indexes;
      if (!filtered.length) return null;
      const mainIncluded = selectedSet
        ? selectedSet.has(group.main.resultIndex)
        : true;
      if (!mainIncluded && group.details.length === 0) return null;
      return [...new Set(filtered)].sort((a, b) => a - b);
    })
    .filter(Boolean);
}
